import { useCallback } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHotelRoutePaths, useHotelBookingRoutePaths, UtmParameters } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { AccommodationListingDTO, AccommodationsConsoleState } from '../AccommodationsConsole.types';
import { AccommodationCatalogUnitOccupancyInput } from '@graphql/generated';

/**
 * Formats a room's occupancy into the URL parameter format
 * @example
 * // returns "a2:c5:c7" for a room with 2 adults and 2 children aged 5 and 7
 * formatRoomParameter({ adults: 2, childrenAges: [5, 7] })
 */
const formatRoomParameter = (room: AccommodationCatalogUnitOccupancyInput): string => {
  const adultsParam = `a${room.adults}`;
  const childrenParam = room.childrenAges?.length ? `:c${room.childrenAges.join(':c')}` : '';
  return `${adultsParam}${childrenParam}`;
};

export const useAccommodationRedirectUrl = (state: AccommodationsConsoleState) => {
  const { eventHandle } = useEventInfo();
  const hotelRoute = useHotelRoutePaths();
  const { buildPath, roomblock } = useHotelBookingRoutePaths();

  return useCallback(
    (listing: AccommodationListingDTO) => {
      const paramsData: UtmParameters = {
        utm_medium: 'web',
        utm_source: 'website_accommodations_console_page',
        utm_campaign: 'see_availability',
        ...(state.searchDates && {
          checkIn: state.searchDates.checkin,
          checkOut: state.searchDates.checkout
        }),
        ...(state.occupancies?.occupancies?.length && {
          rooms: state.occupancies.occupancies.map(formatRoomParameter).join(',')
        }),
        ...(state.searchDates &&
          state.occupancies?.occupancies?.length && {
            skipIfNoSearchParams: true
          })
      };

      if (listing.type === 'roomBlock') {
        return buildPath(eventHandle || '', roomblock, listing.id);
      }

      if (listing.isPickedByCouple) {
        return hotelRoute.accommodationPlace.buildRelativeUrl(eventHandle || '', listing.id || '', paramsData);
      }
      return hotelRoute.hotelDetails.buildRelativeUrl(eventHandle || '', listing.joyPlaceId || '', paramsData);
    },
    [buildPath, eventHandle, hotelRoute.accommodationPlace, hotelRoute.hotelDetails, roomblock, state.occupancies?.occupancies, state.searchDates]
  );
};
